import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Mail,
  Shield,
  RefreshCw,
  Trash2,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Loader2,
  Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Google Workspace Icon Component
const GoogleWorkspaceIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Microsoft 365 Icon Component
const Microsoft365Icon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
    <path d="M11.5 2H2v9.5h9.5V2z" fill="#F25022"/>
    <path d="M22 2h-9.5v9.5H22V2z" fill="#7FBA00"/>
    <path d="M11.5 12.5H2V22h9.5v-9.5z" fill="#00A4EF"/>
    <path d="M22 12.5h-9.5V22H22v-9.5z" fill="#FFB900"/>
  </svg>
);

export default function IntegrationsSettings() {
  const [searchParams] = useSearchParams();
  const [googleIntegrations, setGoogleIntegrations] = useState([]);
  const [microsoftIntegrations, setMicrosoftIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [connectingMicrosoft, setConnectingMicrosoft] = useState(false);
  const [scanning, setScanningId] = useState(null);
  const [disconnectDialog, setDisconnectDialog] = useState({ open: false, integration: null, provider: null });
  const [scanResults, setScanResults] = useState(null);

  useEffect(() => {
    fetchAllIntegrations();
    
    // Check for OAuth callback results
    const googleStatus = searchParams.get('google');
    const microsoftConnected = searchParams.get('microsoft_connected');
    const email = searchParams.get('email');
    const message = searchParams.get('message');
    const error = searchParams.get('error');
    
    if (googleStatus === 'connected') {
      toast.success(`Google Workspace connected: ${email}`);
      fetchAllIntegrations();
    } else if (googleStatus === 'error') {
      toast.error(`Google connection failed: ${message || 'Unknown error'}`);
    }
    
    if (microsoftConnected === 'true') {
      toast.success(`Microsoft 365 connected: ${email}`);
      fetchAllIntegrations();
    } else if (error) {
      toast.error(`Connection failed: ${error}`);
    }
  }, [searchParams]);

  const fetchAllIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch Google integrations
      const googleResponse = await axios.get(`${API}/api/integrations/google`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleIntegrations(googleResponse.data.integrations || []);
      
      // Fetch Microsoft integrations
      const microsoftResponse = await axios.get(`${API}/api/integrations/microsoft`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMicrosoftIntegrations(microsoftResponse.data.integrations || []);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectGoogleWorkspace = async () => {
    setConnectingGoogle(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/google/auth-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to initiate Google connection');
      setConnectingGoogle(false);
    }
  };

  const connectMicrosoft365 = async () => {
    setConnectingMicrosoft(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/microsoft/auth-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to initiate Microsoft connection';
      toast.error(errorMessage);
      setConnectingMicrosoft(false);
    }
  };

  const disconnectIntegration = async () => {
    if (!disconnectDialog.integration || !disconnectDialog.provider) return;
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = disconnectDialog.provider === 'google' 
        ? `/api/integrations/google/${disconnectDialog.integration.id}`
        : `/api/integrations/microsoft/${disconnectDialog.integration.id}`;
      
      await axios.delete(`${API}${endpoint}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      toast.success(`${disconnectDialog.provider === 'google' ? 'Google Workspace' : 'Microsoft 365'} disconnected`);
      fetchAllIntegrations();
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnectDialog({ open: false, integration: null, provider: null });
    }
  };

  const scanEmails = async (integrationId, provider) => {
    setScanningId(integrationId);
    setScanResults(null);
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = provider === 'google'
        ? `/api/integrations/google/${integrationId}/scan?max_results=50&hours_back=24`
        : `/api/integrations/microsoft/${integrationId}/scan?max_emails=50&hours_back=24`;
      
      const response = await axios.post(`${API}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setScanResults({ ...response.data, provider });
      const scanned = response.data.scanned_count || response.data.emails_scanned || 0;
      const threats = response.data.threats_found || response.data.threats_detected || 0;
      toast.success(`Scanned ${scanned} emails, found ${threats} threats`);
      fetchAllIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scan failed');
    } finally {
      setScanningId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>;
      case 'token_expired':
      case 'requires_reauth':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Reconnect Required</Badge>;
      case 'disconnected':
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Disconnected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const renderIntegrationCard = (provider, icon, title, description, integrations, connecting, onConnect) => (
    <Card className="bg-[#1a1a2e] border-gray-800">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg">
              {icon}
            </div>
            <div>
              <CardTitle className="text-white">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
          <Button 
            onClick={onConnect}
            disabled={connecting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {connecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                Connect Account
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : integrations.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-gray-700 rounded-lg">
            <Mail className="w-12 h-12 mx-auto text-gray-600 mb-3" />
            <p className="text-gray-400">No {title} accounts connected</p>
            <p className="text-gray-500 text-sm mt-1">
              Connect an account to start scanning emails for threats
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {integrations.filter(i => i.status !== 'disconnected').map((integration) => (
              <div 
                key={integration.id}
                className="flex items-center justify-between p-4 bg-[#0f0f1a] rounded-lg border border-gray-800"
              >
                <div className="flex items-center gap-4">
                  {integration.picture ? (
                    <img 
                      src={integration.picture} 
                      alt={integration.name || integration.user_name}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      provider === 'google' ? 'bg-blue-600' : 'bg-orange-600'
                    }`}>
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div>
                    <p className="text-white font-medium">{integration.name || integration.user_name || integration.email}</p>
                    <p className="text-gray-400 text-sm">{integration.email}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        {integration.emails_scanned || 0} emails scanned
                      </span>
                      <span className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {integration.threats_detected || 0} threats found
                      </span>
                      {integration.last_sync && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Last scan: {new Date(integration.last_sync).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(integration.status)}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => scanEmails(integration.id, provider)}
                    disabled={scanning === integration.id || integration.status !== 'active'}
                    className="border-gray-700"
                  >
                    {scanning === integration.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Scan Now
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDisconnectDialog({ open: true, integration, provider })}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Integrations</h1>
            <p className="text-gray-400 mt-1">Connect your email providers for automated security scanning</p>
          </div>
        </div>

        {/* Google Workspace Integration */}
        {renderIntegrationCard(
          'google',
          <GoogleWorkspaceIcon />,
          'Google Workspace',
          'Connect Gmail accounts for email security scanning',
          googleIntegrations,
          connectingGoogle,
          connectGoogleWorkspace
        )}

        {/* Microsoft 365 Integration */}
        {renderIntegrationCard(
          'microsoft',
          <Microsoft365Icon />,
          'Microsoft 365',
          'Connect Outlook accounts for email security scanning',
          microsoftIntegrations,
          connectingMicrosoft,
          connectMicrosoft365
        )}

        {/* Scan Results */}
        {scanResults && (
          <Card className="bg-[#1a1a2e] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Scan Results ({scanResults.provider === 'google' ? 'Google' : 'Microsoft'})
              </CardTitle>
              <CardDescription>
                Scanned {scanResults.scanned_count || scanResults.emails_scanned || 0} emails • 
                {scanResults.threats_found || scanResults.threats_detected || 0} threats detected
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(scanResults.threats_found || scanResults.threats_detected || 0) === 0 ? (
                <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                  <div>
                    <p className="text-green-400 font-medium">All Clear!</p>
                    <p className="text-gray-400 text-sm">No threats detected in the last 24 hours</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {(scanResults.threats || []).map((threat, idx) => (
                    <div 
                      key={idx}
                      className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-medium">{threat.subject}</p>
                          <p className="text-gray-400 text-sm">
                            From: {threat.from || threat.sender_email} 
                            {threat.sender_name && ` (${threat.sender_name})`}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {(threat.threats || threat.threat_reasons || []).map((t, i) => (
                              <Badge 
                                key={i}
                                variant="outline" 
                                className="border-red-500/50 text-red-400"
                              >
                                {typeof t === 'string' ? t : `${t.type}: ${t.description}`}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Badge className={
                          (threat.risk_level || threat.classification) === 'critical' ? 'bg-red-600' :
                          (threat.risk_level || threat.classification) === 'high' ? 'bg-orange-600' :
                          'bg-yellow-600'
                        }>
                          {threat.risk_level || threat.classification}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Features Card */}
        <Card className="bg-[#1a1a2e] border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Email Security Features</CardTitle>
            <CardDescription>What gets scanned when you connect your email provider</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-[#0f0f1a] rounded-lg">
                <Shield className="w-8 h-8 text-blue-500 mb-3" />
                <h3 className="text-white font-medium mb-1">Phishing Detection</h3>
                <p className="text-gray-400 text-sm">
                  AI-powered analysis of sender domains, subject lines, and content for phishing attempts
                </p>
              </div>
              <div className="p-4 bg-[#0f0f1a] rounded-lg">
                <AlertTriangle className="w-8 h-8 text-yellow-500 mb-3" />
                <h3 className="text-white font-medium mb-1">Malware Scanning</h3>
                <p className="text-gray-400 text-sm">
                  Detect dangerous attachments and suspicious file types before they reach users
                </p>
              </div>
              <div className="p-4 bg-[#0f0f1a] rounded-lg">
                <Mail className="w-8 h-8 text-green-500 mb-3" />
                <h3 className="text-white font-medium mb-1">Impersonation Detection</h3>
                <p className="text-gray-400 text-sm">
                  Identify emails spoofing trusted brands like PayPal, Microsoft, and Google
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Disconnect Dialog */}
      <AlertDialog open={disconnectDialog.open} onOpenChange={(open) => setDisconnectDialog({ open, integration: null, provider: null })}>
        <AlertDialogContent className="bg-[#1a1a2e] border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Disconnect {disconnectDialog.provider === 'google' ? 'Google Workspace' : 'Microsoft 365'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove access to {disconnectDialog.integration?.email}. 
              You can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={disconnectIntegration}
              className="bg-red-600 hover:bg-red-700"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
